import { type PropsWithChildren, Children } from 'react';

import { wrapper } from './AnnotationsAtom.sty.js';
import { AnnotationsCanvasLayout } from './components/canvas/AnnotationsCanvasLayout.js';
import { CanvasSvgToImagesProvider } from './components/canvas/providers/CanvasSvgToImages.Provider.js';
import { AnnotationsOverlays } from './components/overlays/AnnotationsOverlays.js';
import {
  DEFAULT_LABELS_WIDTH,
  TRACK_SEPARATOR_STROKE_WIDTH,
} from './constants.js';
import { AnnotationsConfigContext } from './contexts/AnnotationsConfig.context.js';
import { MarkerCoordinatesContext } from './contexts/MarkerCoordinates.context.js';
import { MarkerEventHandlersContext } from './contexts/MarkerEventHandlers.context.js';
import { MarkersByTrackContext } from './contexts/MarkersByTrack.context.js';
import { TrackEventHandlersContext } from './contexts/TrackEventHandlers.context.js';
import { MarkerAtom } from './slots/MarkerAtom.js';
import { TrackAtom } from './slots/TrackAtom.js';
import { aggregateMarkersCoords } from './utils/aggregate-markers-coords.js';
import { extractVisibleTracks } from './utils/extract-visible-tracks.js';
import { getAnnotationsTracksHeight } from './utils/get-annotations-tracks-height.js';

/**
 * Props for AnnotationsAtom, representing properties related to annotations.
 * @internal
 */
export interface AnnotationsAtomProps {
  /** Width of the annotations' container. */
  width: number;
  /**
   * Optional flag to determine whether to display labels.
   * @defaultValue true
   */
  showLabels?: boolean;
  /**
   * Optional width for the labels if displayed.
   * @defaultValue 'DEFAULT_LABELS_WIDTH' constant
   */
  labelsWidth?: number;
}

/**
 * The Annotations atomic component
 * @internal
 */
export const _AnnotationsAtom = (
  props: PropsWithChildren<AnnotationsAtomProps>,
) => {
  const {
    width,
    children,
    showLabels = true,
    labelsWidth = DEFAULT_LABELS_WIDTH,
  } = props;

  const trackWidth = width - labelsWidth;
  const tracks = extractVisibleTracks(children);
  const tracksQuantity = Children.count(tracks);
  const totalTracksHeight = getAnnotationsTracksHeight(tracksQuantity);

  const {
    markersPerTrack,
    markerEventHandlers,
    trackEventHandlers,
    icons,
    tracksConfig,
  } = aggregateMarkersCoords(tracks, trackWidth);

  return (
    <div
      className={wrapper}
      style={{
        height: totalTracksHeight,
      }}
    >
      <AnnotationsConfigContext.Provider
        value={{
          showLabels,
          labelsWidth,
          trackWidth,
        }}
      >
        <MarkersByTrackContext.Provider value={tracksConfig}>
          <MarkerCoordinatesContext.Provider value={markersPerTrack}>
            <MarkerEventHandlersContext.Provider value={markerEventHandlers}>
              <TrackEventHandlersContext.Provider value={trackEventHandlers}>
                <CanvasSvgToImagesProvider icons={icons}>
                  <AnnotationsOverlays
                    height={totalTracksHeight - TRACK_SEPARATOR_STROKE_WIDTH}
                  />
                  <AnnotationsCanvasLayout
                    width={width}
                    height={totalTracksHeight}
                  />
                </CanvasSvgToImagesProvider>
              </TrackEventHandlersContext.Provider>
            </MarkerEventHandlersContext.Provider>
          </MarkerCoordinatesContext.Provider>
        </MarkersByTrackContext.Provider>
      </AnnotationsConfigContext.Provider>
    </div>
  );
};

/**
 * The Annotations atomic component
 * @internal
 **/
export const AnnotationsAtom = Object.assign(_AnnotationsAtom, {
  TrackAtom,
  MarkerAtom,
});

(
  AnnotationsAtom as typeof AnnotationsAtom & { displayName: string }
).displayName = 'AnnotationsAtom';
